import {
  AttachmentForCreateEstimate,
  CreateEstimatePayload,
  GetEstimateAttachmentsResponse,
  LineItemForCreateEstimate,
  ServiceCharge,
  Tax
} from './estimates.model';
import { CreateEstimateErrorTypes, EstimateStatus } from './constants';
import { store } from 'store/store';
import { API_SOURCE, DISCOUNT_TYPE } from 'constants/common';
import { DiscountFormData } from 'containers/invoices/types';
import { DiscountSettings } from 'containers/invoices/invoices.model';
import { LeadSource } from 'containers/jobs/constant';
import { isEqual } from 'lodash';
import { getPercentageAmount, roundToTwoDecimals } from 'utils/amountUtils';
import { getAggregatedTaxInCurrency } from 'containers/invoices/utils';
import { AttachmentSection } from 'components/attachment-library/AttachmentLibrary';
import { JobAttachmentListResponse } from 'containers/jobs/jobs.model';
import { GetInvoiceAttachmentsResponse } from 'containers/invoices/invoices.model';
import DateUtils from 'utils/dateUtils';

export const getBorderStyles = ({ isSelected = false, hasError = false }) => {
  if (hasError) return 'border-error';
  if (isSelected) return 'border-secondary';
  return 'border-borderGray';
};

export const getEstimateValidationErrors = (validations, type: CreateEstimateErrorTypes) => {
  const error = validations.find(item => item.type === type);
  return error?.errorMsg || '';
};

export const prepareEstimateLineItemList = (lineItems: any[]): LineItemForCreateEstimate[] => {
  return lineItems.map(item => ({
    catalogueLineItemId: item.catalogueLineItemId,
    name: item.itemDescription,
    qty: parseFloat(item.quantity),
    amount: parseFloat(item.total),
    unitPrice: parseFloat(item.unitCost),
    unitCost: parseFloat(item.originalUnitCost ?? ''),
    markupPercentage: parseFloat(item.markupPercentage ?? ''),
    sku: item.sku || '',
    additionalDetails: item.additionalDetails || '',
    taxes: (item.taxes || []).map(tax => ({
      name: tax.name,
      percentage: parseFloat(tax.percentage),
      amount: parseFloat(tax.amount),
      catalogueTaxId: tax?.id,
      additionalTaxId: tax?.additionalTaxId
    }))
  }));
};

export const getEstimateAttachmentArray = (attachments: any[]): AttachmentForCreateEstimate[] => {
  return attachments.map(attachment => {
    return {
      title: attachment.title,
      url: attachment.url,
      description: '',
      source: attachment.source ?? 'ESTIMATE'
    };
  });
};

export const prepareEstimateServiceChargeListRequestPayload = (serviceCharge: any): ServiceCharge[] => {
  if (!serviceCharge || !serviceCharge.name) {
    return [];
  }

  let serviceChargeTaxList: ServiceCharge[] = [];
  let serviceChargeBaseAmount = +serviceCharge.totalAmount;
  if (serviceCharge?.taxes?.length > 0) {
    if (+serviceCharge.percentage > 0) {
      serviceChargeBaseAmount = +serviceCharge.totalAmount;
      serviceChargeTaxList = serviceCharge?.taxes?.map(tax => ({
        catalogueTaxId: tax.id,
        name: tax.name,
        percentage: +tax?.percentage || 0,
        amount: roundToTwoDecimals(getPercentageAmount(serviceChargeBaseAmount, tax.percentage)) ?? 0
      }));
    } else if (+serviceCharge.amount > 0) {
      serviceChargeBaseAmount = +serviceCharge.amount;
      serviceChargeTaxList = serviceCharge?.taxes?.map(tax => ({
        catalogueTaxId: tax.id,
        name: tax.name,
        percentage: +tax.percentage || 0,
        amount: roundToTwoDecimals(getPercentageAmount(serviceChargeBaseAmount, tax.percentage)) ?? 0
      }));
    }
  }

  return [
    {
      name: serviceCharge.name,
      amount: +serviceCharge.amount,
      percentage: +serviceCharge.percentage,
      totalAmount: serviceChargeBaseAmount,
      taxes: serviceChargeTaxList
    }
  ];
};

export const prepareDiscountPayloadForEstimate = (
  discountData: DiscountFormData,
  discountSettings: DiscountSettings
): { discount: number; discountType: DISCOUNT_TYPE } => {
  if (discountSettings.enabled) {
    if (discountData.isDiscountAmountSelected) {
      return {
        discount: parseFloat(discountData.discountAmount),
        discountType: DISCOUNT_TYPE.FLAT
      };
    }
    return {
      discount: parseFloat(discountData.discountPercent),
      discountType: DISCOUNT_TYPE.PERCENTAGE
    };
  }
  return {
    discount: 0,
    discountType: DISCOUNT_TYPE.FLAT
  };
};

export const prepareSummatedUniqueTaxList = ({
  appliedLineItemLevelTaxes,
  estimateLineItems,
  selectedServiceCharge
}): { taxes: Tax[] } => {
  if (appliedLineItemLevelTaxes.length > 0) {
    const summatedUniqueTaxes = appliedLineItemLevelTaxes.map(tax => {
      return {
        catalogueTaxId: tax?.id,
        name: tax.name,
        percentage: +tax.percentage || 0,
        amount: parseFloat(getAggregatedTaxInCurrency(estimateLineItems, tax, selectedServiceCharge)) ?? 0
      };
    });
    return { taxes: summatedUniqueTaxes };
  }
  return { taxes: null };
};

export const generateCreateEstimatePayload = (): CreateEstimatePayload => {
  const { rootReducer } = store.getState();
  const { estimates } = rootReducer;

  const {
    estimateTitle,
    estimateDescription,
    estimateCustomer,
    serviceAddress,
    expiryDate,
    estimateLineItems,
    estimateDiscountFormData,
    assignees,
    estimateIsServiceFeeEnabled,
    estimateCurrentSurcharge,
    estimateSelectedServiceCharge,
    attachments,
    notes,
    estimateAppliedLineItemLevelTaxes,
    discountSettings,
    isSignatureEnabled,
    entityId,
    entityNumber,
    isEstimateFromOtherEntities,
    entityType,
    estimateReferenceNumber,
    leadSource,
    leadSourceType,
    estimatesTnc
  } = estimates;

  const discountPayload = prepareDiscountPayloadForEstimate(estimateDiscountFormData, discountSettings);

  const summatedTaxes = prepareSummatedUniqueTaxList({
    appliedLineItemLevelTaxes: estimateAppliedLineItemLevelTaxes,
    estimateLineItems,
    selectedServiceCharge: estimateSelectedServiceCharge
  });

  const payload: CreateEstimatePayload = {
    title: estimateTitle?.trim() || '',
    description: estimateDescription?.trim() || '',
    customerId: estimateCustomer?.ID || '',
    customerServiceAddressId: serviceAddress?.id || 0,
    expiresAt: expiryDate,
    referenceNo: estimateReferenceNumber,
    ...discountPayload,
    isServiceFeeEnabled: estimateIsServiceFeeEnabled,
    surchargeAmount: estimateIsServiceFeeEnabled ? estimateCurrentSurcharge.valueInCurrencyWithBps || 0 : 0,
    serviceChargeAmount: +estimateSelectedServiceCharge?.totalAmount || 0,
    lineItems: prepareEstimateLineItemList(estimateLineItems),
    attachments: getEstimateAttachmentArray(attachments),
    notes: { comment: notes },
    taxes: summatedTaxes.taxes,
    serviceCharges: prepareEstimateServiceChargeListRequestPayload(estimateSelectedServiceCharge),
    isSignatureEnabled,
    tnc: estimatesTnc?.trim() || '',
    assignees: assignees.map(assignee => assignee.merchantId),
    leadSource,
    leadSourceType: leadSourceType?.id ?? null,
    source: API_SOURCE.WEB
  };

  // Add relativeEntity if isFromOtherEntities is true
  if (isEstimateFromOtherEntities) {
    payload.relativeEntity = {
      entityType: entityType, // Use the passed entityType
      entityId: entityId || '',
      entityNumber: entityNumber || ''
    };
  }

  return payload;
};

export function getStatusColor(status: EstimateStatus) {
  switch (status.toUpperCase()) {
    case EstimateStatus.PENDING:
      return '#F2994A';
    case EstimateStatus.ACCEPTED:
      return '#27AE60';
    case EstimateStatus.INVOICED:
      return '#27AE60';
    case EstimateStatus.REJECTED:
      return '#D95945';
    case EstimateStatus.EXPIRED:
      return '#828282';
    default:
      return '#828282';
  }
}

export function getStatusBGColor(status: EstimateStatus) {
  switch (status.toUpperCase()) {
    case EstimateStatus.PENDING:
      return '#FFF1D9';
    case EstimateStatus.ACCEPTED:
    case EstimateStatus.INVOICED:
      return '#DFFFDE';
    case EstimateStatus.REJECTED:
      return '#D9594533';
    case EstimateStatus.EXPIRED:
      return '#ECF1F7';
    default:
      return '#ECF1F7';
  }
}

export function getStatusText(status: EstimateStatus) {
  switch (status.toUpperCase()) {
    case EstimateStatus.PENDING:
      return 'Pending';
    case EstimateStatus.ACCEPTED:
      return 'Accepted';
    case EstimateStatus.REJECTED:
      return 'Rejected';
    case EstimateStatus.EXPIRED:
      return 'Expired';
    case EstimateStatus.ARCHIVED:
      return 'Archived';
    case EstimateStatus.INVOICED:
      return 'Invoiced';
    default:
      return status.toLowerCase();
  }
}

export const validateEstimateFields = () => {
  const { rootReducer } = store.getState();
  const { estimates, app } = rootReducer;

  let isPageIncomplete = false;
  const errors = {
    customer: false,
    serviceAddress: false,
    lineItem: false,
    title: false,
    assignees: false,
    leadSource: false,
    tnc: false,
    totalAmount: false
  };
  const {
    estimateCustomer,
    serviceAddress,
    estimateLineItems,
    assignees,
    leadSourceType,
    leadSource,
    estimateSubTotal
  } = estimates;

  if (!estimateCustomer || !estimateCustomer.ID) {
    isPageIncomplete = true;
    errors.customer = true;
  }

  if (!serviceAddress || !serviceAddress.id) {
    isPageIncomplete = true;
    errors.serviceAddress = true;
  }

  if (!estimateLineItems || estimateLineItems.length === 0) {
    isPageIncomplete = true;
    errors.lineItem = true;
  }

  if (!assignees || assignees.length === 0) {
    isPageIncomplete = true;
    errors.assignees = true;
  }

  // if (!estimateTitle.trim()) {
  //   isPageIncomplete = true;
  //   errors.title = true;
  // }

  if (leadSourceType?.id && [LeadSource.REFERRAL, LeadSource.OTHER].includes(leadSourceType.id) && !leadSource) {
    isPageIncomplete = true;
    errors.leadSource = true;
  }

  const invoiceMinAmount = +app.merchantCustomizations?.minimumAmount || 0;
  if (estimateSubTotal < invoiceMinAmount) {
    isPageIncomplete = true;
    errors.totalAmount = true;
  }

  return { isPageIncomplete, errors };
};

export const hasDataChangesInEstimate = () => {
  const { rootReducer } = store.getState();
  const { estimates } = rootReducer;

  const {
    estimateCustomer,
    serviceAddress: estimateServiceAddress,
    estimateLineItems,
    estimateSelectedServiceCharge,
    billingAddress: estimateBillingAddress,
    estimateSubTotal,
    estimateIsServiceFeeEnabled,
    estimateCurrentSurcharge,
    initialEstimateCustomer,
    initialServiceAddress,
    initialBillingAddress,
    initialEstimateLineItems,
    initialEstimateSelectedServiceCharge,
    initialEstimateSubTotal,
    initialEstimateIsServiceFeeEnabled,
    initialEstimateCurrentSurcharge
  } = estimates;

  if (!isEqual(estimateCustomer, initialEstimateCustomer)) {
    return true;
  }
  if (!isEqual(estimateServiceAddress, initialServiceAddress)) {
    return true;
  }
  if (!isEqual(estimateBillingAddress, initialBillingAddress)) {
    return true;
  }
  if (!isEqual(estimateLineItems, initialEstimateLineItems)) {
    return true;
  }
  if (!isEqual(estimateSelectedServiceCharge, initialEstimateSelectedServiceCharge)) {
    return true;
  }
  if (estimateSubTotal !== initialEstimateSubTotal) {
    return true;
  }
  if (
    !(
      estimateIsServiceFeeEnabled === initialEstimateIsServiceFeeEnabled &&
      (initialEstimateIsServiceFeeEnabled ? isEqual(estimateCurrentSurcharge, initialEstimateCurrentSurcharge) : true)
    )
  ) {
    return true;
  }
  return false;
};

export const generateAttachmentSections = (
  estimateAttachments?: GetEstimateAttachmentsResponse,
  jobAttachments?: JobAttachmentListResponse,
  invoiceAttachments?: GetInvoiceAttachmentsResponse
): AttachmentSection[] => {
  const sections: AttachmentSection[] = [];

  // Add estimate attachments
  if (estimateAttachments?.estimateLevelAttachments?.attachments?.length > 0) {
    sections.push({
      title: 'Related estimate attachments',
      showCheckboxForTitle: true,
      showCheckboxForNote: false,
      attachments: [
        {
          createdAt: estimateAttachments.estimateLevelAttachments.createdAt,
          createdBy: estimateAttachments.estimateLevelAttachments.createdBy,
          attachmentResponse: estimateAttachments.estimateLevelAttachments.attachments.map(attachment => ({
            id: attachment.id.toString(),
            title: attachment.title,
            urlLink: attachment.urlLink,
            url: attachment.url,
            source: attachment.source || 'ESTIMATE'
          }))
        }
      ]
    });
  }

  if (estimateAttachments?.estimateNoteLevelAttachments?.length > 0) {
    sections.push({
      title: 'Related estimate note attachments',
      showCheckboxForTitle: false,
      showCheckboxForNote: true,
      attachments: estimateAttachments.estimateNoteLevelAttachments.map(note => ({
        createdAt: note.createdAt,
        createdBy: note.createdBy,
        comment: note.comment,
        attachmentResponse: note.attachments.map(attachment => ({
          id: attachment.id.toString(),
          title: attachment.title,
          urlLink: attachment.urlLink,
          url: attachment.url,
          source: attachment.source || 'ESTIMATE'
        }))
      }))
    });
  }

  // Add job attachments
  if (jobAttachments?.data?.jobAttachmentList?.attachmentResponse?.length > 0) {
    sections.push({
      title: 'Related job attachments',
      showCheckboxForTitle: true,
      showCheckboxForNote: false,
      attachments: [
        {
          createdAt: jobAttachments.data.jobAttachmentList.createdAt,
          createdBy: jobAttachments.data.jobAttachmentList.createdBy,
          attachmentResponse: jobAttachments.data.jobAttachmentList.attachmentResponse.map(attachment => ({
            id: attachment.id.toString(),
            title: attachment.title,
            urlLink: attachment.urlLink,
            url: attachment.url,
            source: attachment.source || 'JOB'
          }))
        }
      ]
    });
  }

  if (jobAttachments?.data?.jobNoteAttachmentList?.length > 0) {
    sections.push({
      title: 'Related job note attachments',
      showCheckboxForTitle: false,
      showCheckboxForNote: true,
      attachments: jobAttachments.data.jobNoteAttachmentList.map(note => ({
        createdAt: note.createdAt,
        createdBy: note.createdBy,
        comment: note.comment,
        attachmentResponse: note.attachmentResponse.map(attachment => ({
          id: attachment.id.toString(),
          title: attachment.title,
          urlLink: attachment.urlLink,
          url: attachment.url,
          source: attachment.source || 'JOB'
        }))
      }))
    });
  }

  // Add invoice attachments
  if (invoiceAttachments?.invoiceAttachmentList?.attachmentResponse?.length > 0) {
    sections.push({
      title: 'Related invoice attachments',
      showCheckboxForTitle: true,
      showCheckboxForNote: false,
      attachments: [
        {
          createdAt: invoiceAttachments.invoiceAttachmentList.createdAt,
          createdBy: invoiceAttachments.invoiceAttachmentList.createdBy,
          attachmentResponse: invoiceAttachments.invoiceAttachmentList.attachmentResponse.map(attachment => ({
            id: attachment.id.toString(),
            title: attachment.title,
            urlLink: attachment.urlLink,
            url: attachment.url,
            source: attachment.source || 'INVOICE'
          }))
        }
      ]
    });
  }

  return sections;
};

export const getExpiryTextForEstimate = (expiresOn: string | Date) => {
  if (!expiresOn) return '';
  const expiryDate = new Date(expiresOn);
  const diffDays = DateUtils.getDifferenceInDays({
    primaryDate: expiryDate,
    secondaryDate: new Date()
  });
  if (diffDays < 0) return 'Expired';
  if (diffDays === 0) return 'Expires today';
  if (diffDays === 1) return 'Expires tomorrow';
  return `Expires in ${diffDays} days`;
};
